// V14.9 — Objectives tab with expandable KPI rows + KPI/Objective notes.
// Each objective is a row that can expand to reveal its KPIs (imported from
// Excel/CSV). Each KPI has its own [View] → opens KPI notes thread. Objective
// itself still has [View] (quick details) and [Notes] (objective-level when no
// KPIs exist, otherwise the user picks a KPI first).
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Plus, Flag, Trash2, Upload, ChevronDown, ChevronRight, MessageSquare, Loader2, Pencil } from 'lucide-react';
import { objectivesApi } from '../../../lib/api';
import { toast } from 'sonner';
import ObjectiveKPINotesModal from '../ObjectiveKPINotesModal';

export function ObjectivesTab({
  objectives, isController, isObserver, setObjectiveCreateOpen, setViewObjective, setObjectiveViewOpen,
  handleDeleteObjective, exerciseStatus, exerciseId, openEditObjective,
}) {
  const [expanded, setExpanded] = useState(new Set());
  const [kpisByObj, setKpisByObj] = useState({}); // obj_id → KPI[]
  const [loadingKpis, setLoadingKpis] = useState(new Set());
  const [importing, setImporting] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesObjective, setNotesObjective] = useState(null);
  const [notesKpi, setNotesKpi] = useState(null);
  const [notesInitialEvalId, setNotesInitialEvalId] = useState(null);
  // V14.10.7 — per-objective note count summary (loaded on expand)
  const [noteCountsByObj, setNoteCountsByObj] = useState({}); // obj_id → counts dict
  const [seenNoteCounts, setSeenNoteCounts] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`ems_objnote_seen_${exerciseId || 'g'}`) || '{}'); }
    catch { return {}; }
  });
  const markObjNotesSeen = useCallback((objId, currentTotal) => {
    setSeenNoteCounts(prev => {
      const next = { ...prev, [objId]: currentTotal };
      try { localStorage.setItem(`ems_objnote_seen_${exerciseId || 'g'}`, JSON.stringify(next)); } catch {}
      return next;
    });
  }, [exerciseId]);
  const fileInputRef = React.useRef(null);

  const loadNoteCounts = useCallback(async (objId) => {
    try {
      const c = await objectivesApi.noteCounts(objId);
      setNoteCountsByObj(prev => ({ ...prev, [objId]: c }));
    } catch { /* ignore */ }
  }, []);

  const loadKpis = useCallback(async (objId) => {
    if (kpisByObj[objId] !== undefined) return; // already loaded (even if [])
    setLoadingKpis(prev => new Set(prev).add(objId));
    try {
      const data = await objectivesApi.listKpis(objId);
      setKpisByObj(prev => ({ ...prev, [objId]: data || [] }));
    } catch {
      setKpisByObj(prev => ({ ...prev, [objId]: [] }));
    } finally {
      setLoadingKpis(prev => { const n = new Set(prev); n.delete(objId); return n; });
    }
  }, [kpisByObj]);

  // V14.9.2 — KPI count badge sums section_evaluations across all KPIs (each
  // section eval is treated as its own KPI row in the UI, per client request).
  // Builds a flat list of "rows" — one per section_evaluation, OR a fallback
  // row when a KPI has no evals.
  const flattenKpiRows = (kpis) => {
    const rows = [];
    for (const k of kpis) {
      const evs = k.section_evaluations || [];
      if (evs.length === 0) {
        rows.push({ rowKey: `kpi-${k.kpi_id}`, kpi: k, ev: null });
      } else {
        for (const ev of evs) {
          rows.push({ rowKey: `eval-${ev.eval_id}`, kpi: k, ev });
        }
      }
    }
    return rows;
  };

  // Eagerly load KPIs for every objective on mount so we can show counts and
  // pick the right Notes button behaviour (KPI vs objective-level).
  useEffect(() => {
    objectives.forEach(o => loadKpis(o.objective_id));
  }, [objectives, loadKpis]);

  // Load note counts for ALL objectives in one go (cheap aggregator)
  useEffect(() => {
    objectives.forEach(o => { if (!(o.objective_id in noteCountsByObj)) loadNoteCounts(o.objective_id); });
    // eslint-disable-next-line
  }, [objectives]);

  const toggleExpand = (objId) => {
    setExpanded(prev => {
      const n = new Set(prev);
      if (n.has(objId)) n.delete(objId); else { n.add(objId); loadKpis(objId); }
      return n;
    });
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const result = await objectivesApi.importKpis(exerciseId, file);
      toast.success(
        `Imported ${result.objectives_created} new, updated ${result.objectives_updated} · ${result.kpis_created} KPIs`
      );
      // Reset KPI cache so newly imported KPIs show up
      setKpisByObj({});
      // Force parent to reload objectives list (assumes parent re-fetches via prop or visibility)
      window.dispatchEvent(new CustomEvent('objectives:reload'));
    } catch (err) {
      toast.error(err?.detail || err?.message || 'Import failed');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const openObjectiveNotes = (obj) => {
    const total = noteCountsByObj[obj.objective_id]?.total || 0;
    markObjNotesSeen(obj.objective_id, total);
    setNotesObjective(obj); setNotesKpi(null); setNotesInitialEvalId(null); setNotesOpen(true);
  };
  const openKpiNotes = (obj, kpi, ev = null) => {
    const total = noteCountsByObj[obj.objective_id]?.total || 0;
    markObjNotesSeen(obj.objective_id, total);
    setNotesObjective(obj); setNotesKpi(kpi);
    setNotesInitialEvalId(ev?.eval_id || null);
    setNotesOpen(true);
  };

  // After notes modal closes, refresh count so badge stays accurate
  const handleNotesModalChange = (open) => {
    setNotesOpen(open);
    if (!open && notesObjective?.objective_id) {
      loadNoteCounts(notesObjective.objective_id);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">Objectives for this exercise</p>
        {isController() && (
          <div className="flex gap-2">
            <input ref={fileInputRef} type="file" accept=".csv,.tsv,.xlsx,.xlsm,text/csv,text/tab-separated-values"
              className="hidden" onChange={handleImport} data-testid="objectives-import-input" />
            <Button variant="outline" disabled={importing}
              onClick={() => fileInputRef.current?.click()}
              data-testid="objectives-import-btn">
              {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Import Excel/CSV
            </Button>
            <Button variant="outline" onClick={() => setObjectiveCreateOpen(true)} data-testid="exercise-create-objective-btn">
              <Plus className="w-4 h-4 mr-2" /> Create Objective
            </Button>
          </div>
        )}
      </div>

      {objectives.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center text-muted-foreground">No objectives imported for this exercise</CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {objectives.map((obj) => {
            const kpis = kpisByObj[obj.objective_id] || [];
            const rows = flattenKpiRows(kpis);
            const totalCount = rows.length;
            const isOpen = expanded.has(obj.objective_id);
            const isLoadingKpis = loadingKpis.has(obj.objective_id);
            return (
              <Card key={obj.objective_id} className="hover:border-primary/30 transition-colors">
                <CardContent className="p-0">
                  {/* Header row */}
                  <div className="p-4 flex items-start justify-between gap-4">
                    <button
                      className="flex items-start gap-2 min-w-0 flex-1 text-left"
                      onClick={() => toggleExpand(obj.objective_id)}
                      data-testid={`objective-expand-${obj.objective_id}`}
                    >
                      <div className="mt-0.5 shrink-0">
                        {isOpen ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className="capitalize">{obj.objective_code}</Badge>
                          {totalCount > 0 && (
                            <Badge variant="secondary" className="text-[10px]">
                              {totalCount} KPI{totalCount === 1 ? '' : 's'}
                            </Badge>
                          )}
                          {/* V14.10.7 — Notes count badge with blink-on-new */}
                          {(noteCountsByObj[obj.objective_id]?.total || 0) > 0 && (
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                                (noteCountsByObj[obj.objective_id]?.total || 0) > (seenNoteCounts[obj.objective_id] || 0)
                                  ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30 animate-pulse ring-1 ring-amber-400/40'
                                  : 'bg-muted text-muted-foreground border border-border'
                              }`}
                              title={`${noteCountsByObj[obj.objective_id].total} comment${noteCountsByObj[obj.objective_id].total === 1 ? '' : 's'}${
                                (noteCountsByObj[obj.objective_id]?.total || 0) > (seenNoteCounts[obj.objective_id] || 0)
                                  ? ' (new — open Notes to mark as read)' : ''
                              }`}
                              data-testid={`objective-notes-count-${obj.objective_id}`}
                            >
                              <MessageSquare className="w-2.5 h-2.5" />
                              {noteCountsByObj[obj.objective_id].total}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{obj.objective_text || '-'}</p>
                      </div>
                    </button>
                    <div className="flex gap-2 shrink-0">
                      <Button variant="outline" size="sm"
                        onClick={() => { setViewObjective(obj); setObjectiveViewOpen(true); }}
                        data-testid={`objective-view-${obj.objective_id}`}>
                        <Flag className="w-4 h-4 mr-2" /> View
                      </Button>
                      {isController() && openEditObjective && (
                        <Button variant="outline" size="sm"
                          onClick={() => openEditObjective(obj)}
                          data-testid={`objective-edit-${obj.objective_id}`}
                          title="Edit objective"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                      )}
                      {/* Objective-level Notes only when no KPIs exist (per design) */}
                      {kpis.length === 0 && !isLoadingKpis && (
                        <Button variant="outline" size="sm"
                          onClick={() => openObjectiveNotes(obj)}
                          data-testid={`objective-notes-${obj.objective_id}`}>
                          <MessageSquare className="w-4 h-4 mr-2" /> Notes
                        </Button>
                      )}
                      {isController() && handleDeleteObjective && (
                        <Button variant="outline" size="sm"
                          className="text-red-600 border-red-300 hover:bg-red-50 dark:hover:bg-red-950/40"
                          data-testid={`objective-delete-${obj.objective_id}`}
                          onClick={() => {
                            const isActive = exerciseStatus === 'active';
                            const msg = isActive
                              ? `⚠ The exercise is RUNNING. Are you absolutely sure you want to remove objective "${obj.objective_code}"? This cannot be undone.`
                              : `Remove objective "${obj.objective_code}" from this exercise? This cannot be undone.`;
                            if (window.confirm(msg)) handleDeleteObjective(obj.objective_id);
                          }}
                          title="Remove from exercise"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* KPI rows — flattened so each section_evaluation is its
                      own "KPI" row with its own [View] (per client design) */}
                  {isOpen && (
                    <div className="border-t bg-muted/30">
                      {isLoadingKpis ? (
                        <div className="px-4 py-3 text-xs text-muted-foreground flex items-center gap-2">
                          <Loader2 className="w-3 h-3 animate-spin" /> Loading KPIs…
                        </div>
                      ) : rows.length === 0 ? (
                        <div className="px-4 py-3 text-xs text-muted-foreground">
                          No KPIs imported for this objective yet. Use <strong>Import Excel/CSV</strong> above to add them.
                        </div>
                      ) : (
                        <div className="divide-y">
                          {rows.map(({ rowKey, kpi, ev }) => (
                            <div key={rowKey} className="px-4 py-3 flex items-start justify-between gap-3"
                              data-testid={`kpi-row-${rowKey}`}>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <Badge variant="outline" className="text-[10px]">
                                    KPI {kpi.kpi_code || ''}
                                  </Badge>
                                  {ev?.section && (
                                    <span className="px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-700 dark:text-blue-300 border border-blue-500/30 text-[10px] font-medium">
                                      {ev.section}
                                    </span>
                                  )}
                                  {ev?.type && (
                                    <span className="px-1.5 py-0.5 rounded bg-muted border text-[10px]">{ev.type}</span>
                                  )}
                                  {/* V14.10.7 — KPI-level note count badge */}
                                  {(() => {
                                    const counts = noteCountsByObj[obj.objective_id];
                                    if (!counts) return null;
                                    const n = counts.by_eval?.[ev?.eval_id] || counts.by_kpi?.[kpi.kpi_id] || 0;
                                    const atts = counts.by_kpi_attachments?.[kpi.kpi_id] || 0;
                                    if (!n && !atts) return null;
                                    return (
                                      <>
                                        {n > 0 && (
                                          <span
                                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/30"
                                            title={`${n} comment${n === 1 ? '' : 's'}`}
                                            data-testid={`kpi-notes-count-${kpi.kpi_id}`}
                                          >
                                            <MessageSquare className="w-2.5 h-2.5" />
                                            {n}
                                          </span>
                                        )}
                                        {atts > 0 && (
                                          <span
                                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-500/30"
                                            title={`${atts} attachment${atts === 1 ? '' : 's'}`}
                                          >
                                            <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 17.93 8.8L9.41 17.32a2 2 0 1 1-2.83-2.83l8.49-8.48"/></svg>
                                            {atts}
                                          </span>
                                        )}
                                      </>
                                    );
                                  })()}
                                </div>
                                <p className="text-sm mt-1 whitespace-pre-wrap">{kpi.kpi_text}</p>
                                {ev?.evaluation_question && (
                                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                    <span className="font-medium">Q:</span> {ev.evaluation_question}
                                  </p>
                                )}
                              </div>
                              <Button size="sm" variant="outline"
                                onClick={() => openKpiNotes(obj, kpi, ev)}
                                data-testid={`kpi-view-${rowKey}`}>
                                <Flag className="w-3.5 h-3.5 mr-1.5" /> View
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <ObjectiveKPINotesModal
        open={notesOpen}
        onOpenChange={handleNotesModalChange}
        exerciseId={exerciseId}
        objective={notesObjective}
        kpi={notesKpi}
        initialEvalId={notesInitialEvalId}
        readOnly={isObserver ? isObserver() : false}
      />
    </div>
  );
}
